import { useCallback } from 'react';

import { useMutation } from '@zenbusiness/application-commons-graphql';
import { useIdentity } from '@zenbusiness/application-commons-identity';
import { useCaptureException } from '@zenbusiness/application-commons-logging';

import { useUnifiedBusinessEntityContext } from '#client/contexts/UnifiedBusinessEntityProvider';
import { GENERATE_ARTICLE_MUTATION } from '#client/mutations/generateArticle';

/**
 * Custom hook for article generation
 *
 * Handles:
 * - Article generation via GraphQL mutation
 * - Loading states
 * - Error handling with retry logic
 * - Thread integration
 * - Rich error context logging
 */

export function useGenerateArticle() {
  const { businessEntityUuid } = useUnifiedBusinessEntityContext();
  const { userUuid } = useIdentity();

  // Initialize captureException with static tags for better performance
  const captureException = useCaptureException({
    tags: {
      frontend: 'assistant-ui',
      operationName: 'generateArticle'
    }
  });

  const [generateArticleMutation, { loading, error }] = useMutation(GENERATE_ARTICLE_MUTATION, {
    noThrow: true,
    onError: (err) => {
      captureException(err, {
        tags: {
          errorType: 'graphql_mutation',
          businessEntityUuid: businessEntityUuid || 'unknown'
        },
        contexts: {
          articleGeneration: {
            businessEntityUuid,
            userUuid
          },
          mutationError: {
            message: err.message,
            networkError: err.networkError,
            graphQLErrors: err.graphQLErrors
          }
        }
      });
    }
  });

  /**
   * Generates an article based on topic and summary
   * @param {string} topic - Article topic
   * @param {string} summary - Article summary
   * @param {string} threadUuid - Thread UUID to associate article with
   * @returns {Promise<{threadUuid: string, data: Array, ok: boolean, error: any}>}
   */
  const generateArticle = useCallback(
    async (topic, summary, threadUuid, messageUuid) => {
      const entityUuid = businessEntityUuid;

      try {
        const result = await generateArticleMutation({
          variables: {
            identity: {
              userUuid,
              businessEntityUuid: entityUuid
            },
            input: {
              topic,
              summary
            },
            threadUuid,
            messageUuid
          }
        });
        return {
          threadUuid: result.data?.generateArticle?.data[0]?.attributes?.threadUuid,
          data:
            result.data?.generateArticle?.data[0]?.attributes?.uiInstructions[0]?.props?.message ||
            [],
          ok: result.data?.generateArticle?.ok,
          error: result.data?.generateArticle?.error || error // Include the error from response payload
        };
      } catch (err) {
        captureException(err, {
          tags: {
            errorType: 'article_generation_failed',
            businessEntityUuid: entityUuid || 'unknown',
            threadUuid: threadUuid || 'unknown'
          },
          contexts: {
            articleGeneration: {
              topic,
              summary,
              threadUuid,
              businessEntityUuid: entityUuid,
              userUuid,
              errorMessage: err.message,
              errorStack: err.stack
            }
          }
        });
        return null;
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [businessEntityUuid, userUuid, generateArticleMutation]
  );

  return {
    // State
    loading,
    error,

    // Actions
    generateArticle,

    // Computed values
    isNetworkError: error?.message?.includes('fetch') || error?.message?.includes('Network'),
    isAuthError: error?.message?.includes('401') || error?.message?.includes('authentication')
  };
}
